import compact from 'lodash.compact';
import get from 'lodash.get';
import { getUrlParams } from '../shared/urlUtils';
import {
    getInstructionHelpParam,
    getProgressHelpParam,
    isPageAccessibleForHelpType,
} from '../shared/helpUtils';
import {
    URL,
    BASE_URLS,
    SS_URL,
    getD8WithAndWithoutTRCHelp,
} from '../../constants/reading/ApiConstants';
import { PERIOD } from '../../constants/shared/CommonConstants';
import { LICENSE } from '../../constants/reading/CommonConstants';

const { OL, D8, VOCABO, TRC, RAN, SPELLING, EXPRESS } = LICENSE;
const { SUMMARY } = PERIOD;
const { BENCHMARK_URL, PROGRESS_URL, INSTRUCTIONS_URL, HOME_CONNECT_URL, DUAL_LANGUAGE_URL } =
    BASE_URLS;
const { BENCHMARK_HISTORY, PROGRESS, TRC_SUMMARY } = SS_URL;

export const RAN_TITLE = 'RAN';
export const SPELL_TITLE = 'Spelling';
export const TRO_TITLE = 'Text Reading Online';
export const DO_TITLE = 'Decoding Online';
export const EXPRESS_TITLE = 'mCLASS Express';
export const VOCABO_TITLE = 'Vocabulary';
export const OL_TITLE = 'Oral Language';
export const DIBELS8_WITHOUT_TRC_TITLE = 'DIBELS 8th Edition';
export const DIBELS8_WITH_TRC_TITLE = 'DIBELS 8th Edition and TRC';

const d8HelpPageMapper = (period, progressTab, instructionTab) => ({
    [BENCHMARK_URL]: `BCS${period === SUMMARY ? '' : '_TOY'}`,
    [PROGRESS_URL]: getProgressHelpParam(progressTab),
    [INSTRUCTIONS_URL]: getInstructionHelpParam(instructionTab),
    [HOME_CONNECT_URL]: 'CS_HC',
    [BENCHMARK_HISTORY]: 'SS_BH',
    [PROGRESS]: 'SS_PM',
    [DUAL_LANGUAGE_URL]: 'DL',
});

const d8WithTrcHelpPageMapper = (period, progressTab, instructionTab) => ({
    ...d8HelpPageMapper(period, progressTab, instructionTab),
    [TRC_SUMMARY]: 'SS_TRC',
});

const expressHelpPageMapper = (period, instructionTab) => ({
    [BENCHMARK_URL]: period === SUMMARY ? 'BCS' : 'BCS_TOY',
    [INSTRUCTIONS_URL]: getInstructionHelpParam(instructionTab),
    [HOME_CONNECT_URL]: 'CS_HC',
    [BENCHMARK_HISTORY]: 'SS_BH',
});

export const olsHelpPageMapper = period => ({
    [BENCHMARK_URL]: period === SUMMARY ? 'BCS' : 'BCS_TOY',
    [HOME_CONNECT_URL]: 'CS_HC',
    [BENCHMARK_HISTORY]: 'SS_BH',
});

export const dyslexiaHelpPageMapper = period => ({
    [BENCHMARK_URL]: `CS${period === SUMMARY ? '' : '_TOY'}`,
    [BENCHMARK_HISTORY]: 'SS',
});

const vocaboHelpForDLPageMapper = period => ({
    [DUAL_LANGUAGE_URL]: 'DL',
    [BENCHMARK_URL]: `CS${period === SUMMARY ? '' : '_TOY'}`,
    [BENCHMARK_HISTORY]: 'SS',
});

const helpUrls = ({
    pathname,
    period,
    progressTab,
    instructionTab,
    lrsSelectHelpItem,
    getD8Help,
}) => ({
    DIBELS8: {
        title: DIBELS8_WITHOUT_TRC_TITLE,
        url: `${getD8Help}${get(d8HelpPageMapper(period, progressTab, instructionTab), pathname)}`,
        onClick: () => lrsSelectHelpItem(DIBELS8_WITHOUT_TRC_TITLE),
    },
    DIBELS8_WITH_TRC: {
        title: DIBELS8_WITH_TRC_TITLE,
        url: `${getD8Help}${get(
            d8WithTrcHelpPageMapper(period, progressTab, instructionTab),
            pathname,
        )}`,
        onClick: () => lrsSelectHelpItem(DIBELS8_WITH_TRC_TITLE),
    },
    [EXPRESS]: {
        title: EXPRESS_TITLE,
        url: `${URL.EXPRESS.EXPRESS_HELP_URL}${get(
            expressHelpPageMapper(period, instructionTab),
            pathname,
        )}`,
        onClick: () => lrsSelectHelpItem(EXPRESS_TITLE),
    },
    [VOCABO]: {
        title: VOCABO_TITLE,
        url: `${URL.VOCABO.VOCABO_HELP_URL}${get(vocaboHelpForDLPageMapper(period), pathname)}`,
        onClick: () => lrsSelectHelpItem(VOCABO_TITLE),
    },
    [SPELLING]: {
        title: SPELL_TITLE,
        url: `${URL.SPELLING.SPELLING_HELP_URL}${get(dyslexiaHelpPageMapper(period), pathname)}`,
        onClick: () => lrsSelectHelpItem(SPELL_TITLE),
    },
    [RAN]: {
        title: RAN_TITLE,
        url: `${URL.RAN.RAN_HELP_URL}${get(dyslexiaHelpPageMapper(period), pathname)}`,
        onClick: () => lrsSelectHelpItem(RAN_TITLE),
    },
    [OL]: {
        title: OL_TITLE,
        url: `${URL.OL.OL_HELP_URL}${get(olsHelpPageMapper(period), pathname)}`,
        onClick: () => lrsSelectHelpItem(OL_TITLE),
    },
});

const buildHelpItems = (licensingInfo, location, lrsSelectHelpItem) => {
    const { pathname } = location;
    const { period, progressView: progressTab, instructionTab } = getUrlParams(location);
    const { getD8WithoutTrcHelp, getD8WithTrcHelp } = getD8WithAndWithoutTRCHelp(licensingInfo);

    return compact([
        licensingInfo.includes(D8) &&
            !licensingInfo.includes(TRC) &&
            isPageAccessibleForHelpType(d8HelpPageMapper(), pathname) &&
            helpUrls({
                pathname,
                period,
                progressTab,
                instructionTab,
                lrsSelectHelpItem,
                getD8Help: getD8WithoutTrcHelp,
            }).DIBELS8,

        licensingInfo.includes(D8) &&
            licensingInfo.includes(TRC) &&
            isPageAccessibleForHelpType(d8WithTrcHelpPageMapper(), pathname) &&
            helpUrls({
                pathname,
                period,
                progressTab,
                instructionTab,
                lrsSelectHelpItem,
                getD8Help: getD8WithTrcHelp,
            }).DIBELS8_WITH_TRC,

        licensingInfo.includes(OL) &&
            isPageAccessibleForHelpType(olsHelpPageMapper(), pathname) &&
            helpUrls({ pathname, period, lrsSelectHelpItem })[OL],

        licensingInfo.includes(EXPRESS) &&
            isPageAccessibleForHelpType(expressHelpPageMapper(), pathname) &&
            helpUrls({ pathname, period, progressTab, instructionTab, lrsSelectHelpItem })[EXPRESS],

        licensingInfo.includes(VOCABO) &&
            isPageAccessibleForHelpType(vocaboHelpForDLPageMapper(), pathname) &&
            helpUrls({ pathname, period, lrsSelectHelpItem })[VOCABO],

        licensingInfo.includes(SPELLING) &&
            isPageAccessibleForHelpType(dyslexiaHelpPageMapper(), pathname) &&
            helpUrls({ pathname, period, lrsSelectHelpItem })[SPELLING],

        licensingInfo.includes(RAN) &&
            isPageAccessibleForHelpType(dyslexiaHelpPageMapper(), pathname) &&
            helpUrls({ pathname, period, lrsSelectHelpItem })[RAN],
    ]);
};

export default buildHelpItems;
